import { useCompanyOverview, CompanyOverviewMetrics } from '@/hooks/useCompanyOverview';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { MetricCard, MetricComparison } from '@/components/reports/MetricCard';
import { calculateChange } from '@/utils/periodComparison';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  DollarSign,
  ShoppingCart,
  Receipt,
  PhoneCall,
  UserPlus,
  ClipboardList,
  TrendingUp,
  XCircle,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react';

interface CompanyOverviewTabProps {
  dateFrom: Date;
  dateTo: Date;
  compareEnabled?: boolean;
}

const CHART_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

export const CompanyOverviewTab = ({
  dateFrom,
  dateTo,
  compareEnabled = false,
}: CompanyOverviewTabProps) => {
  const { timezone } = useTimezoneUtils();
  const { formatMoney: formatCurrency } = useCompanyLocale();
  const { data, isLoading } = useCompanyOverview(dateFrom, dateTo, timezone, compareEnabled);

  const getComparison = (
    key: keyof CompanyOverviewMetrics,
    isPositiveGood = true
  ): MetricComparison | undefined => {
    if (!compareEnabled || !data?.previous || !data.comparisonPeriod) return undefined;
    const change = calculateChange(
      data.current[key] as number,
      data.previous[key] as number
    );
    return { ...change, label: data.comparisonPeriod.label, isPositiveGood };
  };

  if (isLoading) {
    return <div className="h-64 flex items-center justify-center">Loading Company Overview...</div>;
  }

  const m = data?.current;
  if (!m) return null;

  const riskComparison = getComparison('revenueAtRisk', false);
  const RiskDeltaIcon =
    riskComparison?.direction === 'up'
      ? ArrowUpRight
      : riskComparison?.direction === 'down'
        ? ArrowDownRight
        : Minus;

  return (
    <div className="space-y-[22px]">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
        <MetricCard
          title="Total Revenue"
          value={formatCurrency(m.totalRevenue)}
          icon={DollarSign}
          iconBgColor="bg-success/10"
          iconColor="text-success"
          tooltip="Invoice total of jobs completed (closed) in this period"
          comparison={getComparison('totalRevenue')}
        />
        <MetricCard
          title="Total Sales"
          value={m.totalSales}
          icon={ShoppingCart}
          iconBgColor="bg-primary/10"
          iconColor="text-primary"
          tooltip="Number of completed jobs closed in this period"
          comparison={getComparison('totalSales')}
        />
        <MetricCard
          title="Average Ticket"
          value={formatCurrency(m.avgTicket)}
          icon={Receipt}
          iconBgColor="bg-info/10"
          iconColor="text-info"
          tooltip="Revenue per completed job"
          comparison={getComparison('avgTicket')}
        />
        <MetricCard
          title="New Leads"
          value={m.totalLeads}
          icon={UserPlus}
          iconBgColor="bg-primary/10"
          iconColor="text-primary"
          tooltip="Leads received in this period (excl. spam)"
          comparison={getComparison('totalLeads')}
        />
        <MetricCard
          title="Open Jobs"
          value={m.openJobs}
          icon={ClipboardList}
          iconBgColor="bg-warning/10"
          iconColor="text-warning"
          tooltip="Jobs scheduled in this period that are not yet completed or cancelled"
          comparison={getComparison('openJobs')}
        />
        <MetricCard
          title="Call Booking Rate"
          value={`${m.callBookingRate.toFixed(1)}%`}
          subtitle={`${m.bookedLeads} of ${m.totalLeads} leads`}
          icon={PhoneCall}
          iconBgColor="bg-warning/10"
          iconColor="text-warning"
          tooltip="Leads booked into a job ÷ leads received (excl. spam)"
          comparison={getComparison('callBookingRate')}
        />
        <MetricCard
          title="Job Conversion Rate"
          value={`${m.jobConversionRate.toFixed(1)}%`}
          subtitle={`${m.completedOfScheduled} of ${m.scheduledJobs} jobs`}
          icon={TrendingUp}
          iconBgColor="bg-success/10"
          iconColor="text-success"
          tooltip="Completed jobs ÷ all jobs scheduled in this period"
          comparison={getComparison('jobConversionRate')}
        />
        <MetricCard
          title="Cancellations"
          value={m.totalCancellations}
          icon={XCircle}
          valueColor="text-destructive"
          iconBgColor="bg-destructive/10"
          iconColor="text-destructive"
          tooltip="Jobs cancelled during this period"
          comparison={getComparison('totalCancellations', false)}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[7fr_5fr]">
        {/* Cancellation reasons — stacked distribution bar + legend */}
        <Card>
          <CardHeader>
            <CardTitle>Cancellation reasons</CardTitle>
            <CardDescription>Top 5 reasons for jobs cancelled in this period</CardDescription>
          </CardHeader>
          <CardContent>
            {m.cancellationReasons.length > 0 ? (
              <div className="space-y-4">
                <div className="flex h-[10px] w-full gap-[2px] overflow-hidden rounded-full">
                  {m.cancellationReasons.map((r, index) => (
                    <div
                      key={r.reason}
                      className="h-full"
                      style={{
                        flexGrow: r.count,
                        flexBasis: 0,
                        backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                      }}
                      title={`${r.reason}: ${r.count} (${r.percentage.toFixed(0)}%)`}
                    />
                  ))}
                </div>
                <ul className="space-y-2.5">
                  {m.cancellationReasons.map((r, index) => (
                    <li
                      key={r.reason}
                      className="flex items-center justify-between gap-3 text-[13px]"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 flex-shrink-0 rounded-[3px]"
                          style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                        />
                        <span className="truncate">{r.reason}</span>
                      </span>
                      <span className="flex flex-shrink-0 items-baseline gap-1.5 tabular-nums">
                        <span className="font-semibold">{r.count}</span>
                        <span className="text-xs text-muted-foreground">
                          {r.percentage.toFixed(0)}%
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="flex h-32 items-center justify-center text-[13px] text-muted-foreground">
                No cancellations in this period
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue at risk */}
        <div className="space-y-5">
          <Card className="border-l-[3px] border-l-destructive">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground">Revenue at risk</p>
                  <p className="mt-1 text-[22px] font-semibold leading-tight tracking-tight tabular-nums text-destructive">
                    {formatCurrency(m.revenueAtRisk)}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Quoted value of cancelled jobs
                  </p>
                </div>
                <div className="flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-md bg-destructive/10">
                  <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                </div>
              </div>
              {riskComparison && (
                <div className="mt-3 border-t pt-2.5">
                  <div
                    className={cn(
                      'flex items-center gap-1 text-xs font-medium',
                      riskComparison.direction === 'neutral'
                        ? 'text-muted-foreground'
                        : riskComparison.direction === 'up'
                          ? 'text-destructive'
                          : 'text-success'
                    )}
                  >
                    <RiskDeltaIcon className="h-3 w-3" />
                    <span className="truncate">
                      {riskComparison.direction === 'neutral'
                        ? `No change ${riskComparison.label}`
                        : `${riskComparison.percent.toFixed(1)}% ${riskComparison.label}`}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
